import { Typography } from "@suraasa/placebo-ui"
import { Assignment, AssignmentFAQ } from "api/resources/assignments/types"
import { pluralize } from "utils/helpers"

const getWordLimitMessage = ({
  minWordLimit,
  maxWordLimit,
}: {
  minWordLimit?: number | null
  maxWordLimit?: number | null
}) => {
  let limitMessage = "There is no word limit for this assignment."

  if (minWordLimit && maxWordLimit) {
    limitMessage = `Your response should be between ${minWordLimit} and ${maxWordLimit} words to ensure depth and clarity.`
  } else if (minWordLimit) {
    limitMessage = `Your response should have at least ${pluralize(
      "word",
      minWordLimit
    )} to ensure sufficient depth.`
  } else if (maxWordLimit) {
    limitMessage = `Your response should not exceed ${pluralize(
      "word",
      maxWordLimit
    )} to keep it concise and clear.`
  }

  return (
    <Typography className="text-onSurface-500">
      {limitMessage} Focus on quality over quantity—avoid filler content and
      ensure your answer is well-structured and meaningful.
      <br />
      <br />
      Remember, the goal is meaningful learning, not just word count.
    </Typography>
  )
}

const getHowToAttemptMessage = (
  assignment: Assignment,
  config: {
    isThroughMed: boolean
    isThroughPgCTL: boolean
  }
) => {
  const waysToAttempt = (
    <div>
      There are 2 ways to attempt a Skill Evidence (Assignment):
      <ol className="mb-1 list-decimal ps-2.5">
        <li>
          You can write the Skill Evidence (Assignment) directly into the
          portal. Our editor supports various formatting options that can help
          you create Skill Evidences (Assignments) effectively.
        </li>
        <li>
          If you have created Skill Evidence (Assignment) in a Word document,
          you can simply copy and paste it into the editor on the Suraasa
          portal. All the formatting will be preserved.
        </li>
      </ol>
      Alternatively, you can add the Skill Evidences (Assignments) or supporting
      files as attachments (if supported).
    </div>
  )

  if (config.isThroughMed) {
    return (
      <div>
        To successfully complete any Skill Evidence (Assignment), you must
        achieve a minimum average score of 1.8. It is recommended to refer to
        the <b>marking criteria</b> to ensure all required concepts are covered
        in your submission.
        <br />
        <br />
        Your submission may be sent back for rework under the following
        conditions: <br />
        <ol className="list-decimal ps-2.5">
          <li>
            If you score below <b>1.8</b>.
          </li>
          <li>
            If the assignment exceeds the permissible limits of{" "}
            <b>plagiarism</b> or <b>AI-generated content</b>.
          </li>
          <li>If the assignment violates the data privacy policy.</li>
          <li>If important details are missing, such as:</li>
          <ul className="list-disc ps-4">
            <li>
              <b>In a lesson plan:</b> Date of lesson plan, school name, lesson
              topic, grade level, reflection, and actions for improvement. Note:
              The lesson plan details should match with the lesson observation
              form details etc.
            </li>
            <li>
              <b>In a general plan:</b> Date of the plan, target grade level,
              subject, target audience, etc.
            </li>
            <li>
              <b>In a general task:</b> Key details related to the task LO such
              as assessment data, rubrics, etc.
            </li>
          </ul>
        </ol>
        <br />
        {waysToAttempt}
      </div>
    )
  }

  const passingRequired =
    config.isThroughPgCTL &&
    assignment.deliveryAttributes?.assignmentPassingRequired === true

  if (passingRequired) {
    return (
      <div>
        <b>Certification eligibility:</b> You must achieve a minimum GPA of 5
        per module, translating to a recommended score of at least 1.5 on each
        skill evidence task.
        <br />
        <b>Milestone 3 (Placement Support) eligibility:</b> You must attain a
        minimum GPA of 8, translating to a recommended score of at least 2.4 on
        each task.
        <br />
        <br />
        <strong>
          IMPORTANT: To obtain the targeted GPA, refer to the marking criteria
          of each task and ensure all required concepts are covered in your
          submission.
        </strong>
        <br />
        <br />
        Marks will be deducted for the following issues. Please ensure your
        submission is original, complete, and well-presented to avoid penalties.
        <ol className="list-decimal pl-2.5">
          <li>
            Missing or Incorrect Content (e.g., lesson plan missing date, school
            name, or teacher name), missing APA references, or submitting blank
            files
          </li>
          <li>Plagiarised Content</li>
          <li>AI-Generated Content</li>
          <li>Other Penalties</li>
        </ol>
        <br />
        Additional deductions, including but not limited to, the following:
        <ul className="list-disc pl-2.5">
          <li>Data Protection Breach</li>
          <li>Grammatical Errors</li>
          <li>Poor Presentation</li>
          <li>Ethical Violations</li>
        </ul>
        <br />
        {waysToAttempt}
      </div>
    )
  }

  return (
    <div>
      To successfully complete any Skill Evidence (Assignment), it is
      recommended to refer to the marking criteria to ensure all required
      concepts are covered in your submission.
      <br />
      <br />
      Your submission may be sent back for rework under the following
      conditions: <br />
      <br />
      - If the assignment exceeds the permissible limits of plagiarism or
      AI-generated content.
      <br />
      - If the assignment violates the data privacy policy.
      <br />- If important details are missing, such as: In a lesson plan: Date
      of lesson plan, school name, lesson topic, grade level, reflection, and
      actions for improvement.
      <br />
      Note: The lesson plan details should match with the lesson observation
      form details etc.
      <br />
      In a general plan: Date of the plan, target grade level, subject, target
      audience, etc.
      <br />
      In a general task: Key details related to the task LO such as assessment
      data, rubrics, etc.
      <br />
      <br />
      {waysToAttempt}
    </div>
  )
}

const frontendFAQs = (
  assignment: Assignment,
  config: {
    isThroughMed: boolean
    isThroughPgCTL: boolean
  }
): AssignmentFAQ[] => {
  const wordLimitFAQ: AssignmentFAQ = {
    question: "Is there a word limit?",
    answer: getWordLimitMessage(assignment),
  }

  const howToSubmitAssignmentFAQ = {
    question: "How to submit Skill Evidence (Assignment)?",
    answer: getHowToAttemptMessage(assignment, config),
  }

  return [howToSubmitAssignmentFAQ, wordLimitFAQ]
}

export { frontendFAQs, getWordLimitMessage }
